import { useRef, useState, useCallback, useEffect } from 'react';
import { Tag, Plus, Trash2, RefreshCw, Loader2, Pencil, Download, Upload } from 'lucide-react';
import { Modal } from './ui';
import {
  getAdminCategories,
  createAdminCategory,
  updateAdminCategory,
  deactivateAdminCategory,
  exportAdminCategories,
  importAdminCategories,
  type Category,
} from '../api';

export function AdminCategoryCatalog() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    skipped?: number;
    total: number;
    errors?: Array<{ row: number; message: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAdminCategories(false);
      if (response.status && response.data) {
        setCategories(response.data);
      }
    } catch {
      setError('Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async (label: string, description?: string, sortOrder?: number) => {
    try {
      const response = await createAdminCategory({ label, description, sortOrder });
      if (response.status && response.data) {
        setCategories((prev) =>
          [...prev, response.data].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
        );
        setShowCreateModal(false);
      }
    } catch {
      setError('Failed to create category');
    }
  };

  const handleUpdate = async (
    categoryId: string,
    label: string,
    description?: string,
    isActive?: boolean,
    sortOrder?: number
  ) => {
    try {
      const response = await updateAdminCategory(categoryId, {
        label,
        description,
        isActive,
        sortOrder,
      });
      if (response.status && response.data) {
        setCategories((prev) =>
          prev
            .map((c) => (c.categoryId === categoryId ? response.data : c))
            .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
        );
        setEditingCategory(null);
      }
    } catch {
      setError('Failed to update category');
    }
  };

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      setError(null);
      const blob = await exportAdminCategories(false);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `categories-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export categories');
    } finally {
      setExporting(false);
    }
  }, []);

  const handleImport = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        setImporting(true);
        setError(null);
        setImportResult(null);
        const res = await importAdminCategories(file);
        if (res.status && res.data) {
          setImportResult(res.data);
          fetchCategories();
        } else {
          setError('Import failed');
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(msg || (err instanceof Error ? err.message : 'Failed to import categories'));
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    },
    [fetchCategories]
  );

  const handleDeactivate = async (categoryId: string) => {
    if (
      !window.confirm(
        'Deactivate this category? It will no longer appear in the complaint form. Existing complaints keep their category.'
      )
    ) {
      return;
    }
    try {
      const response = await deactivateAdminCategory(categoryId);
      if (response.status) {
        setCategories((prev) =>
          prev.map((c) => (c.categoryId === categoryId ? { ...c, isActive: false } : c))
        );
      }
    } catch {
      setError('Failed to deactivate category');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Complaint Categories</h2>
            <p className="text-sm text-gray-500">
              Manage complaint categories for the submission form and analytics
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchCategories}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Export to Excel"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Import from Excel"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleImport}
              className="hidden"
            />
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Category
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {importResult && (
        <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <p>
            Imported {importResult.created} of {importResult.total} categories.
            {(importResult.skipped ?? 0) > 0 && (
              <span className="text-gray-600"> ({importResult.skipped ?? 0} already existed)</span>
            )}
          </p>
          {importResult.errors && importResult.errors.length > 0 && (
            <p className="mt-1 text-amber-700">
              {importResult.errors.length} row(s) failed:{' '}
              {importResult.errors.map((e) => 'Row ' + e.row + ': ' + e.message).join('; ')}
            </p>
          )}
        </div>
      )}

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No categories yet</p>
            <p className="text-sm mt-1">
              Add categories to enable the Issue Category field in the complaint form.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Add Category
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {categories.map((category) => (
              <div
                key={category.categoryId}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border rounded-lg ${
                  category.isActive ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div>
                  <p
                    className={`font-medium ${category.isActive ? 'text-gray-900' : 'text-gray-500'}`}
                  >
                    {category.label}
                  </p>
                  {category.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">ID: {category.categoryId}</p>
                </div>
                <div className="flex items-center gap-2">
                  {category.isActive && (
                    <>
                      <button
                        onClick={() => setEditingCategory(category)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeactivate(category.categoryId)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateCategoryModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={(label, description, isActive, sortOrder) =>
            handleUpdate(editingCategory.categoryId, label, description, isActive, sortOrder)
          }
        />
      )}
    </div>
  );
}

function CreateCategoryModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (label: string, description?: string, sortOrder?: number) => void;
}) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSaving(true);
    try {
      await onSave(
        label.trim(),
        description.trim() || undefined,
        sortOrder ? parseInt(sortOrder, 10) : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Category">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g., Food Safety"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Food safety incidents"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder="e.g., 10"
            min={0}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !label.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add Category'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditCategoryModal({
  category,
  onClose,
  onSave,
}: {
  category: Category;
  onClose: () => void;
  onSave: (
    label: string,
    description?: string,
    isActive?: boolean,
    sortOrder?: number
  ) => void;
}) {
  const [label, setLabel] = useState(category.label);
  const [description, setDescription] = useState(category.description || '');
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSaving(true);
    try {
      await onSave(
        label.trim(),
        description.trim() || undefined,
        true,
        sortOrder ? parseInt(sortOrder, 10) : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Category">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort Order <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            min={0}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !label.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
